import { IconButton, Icons, Stack, Tooltip, Typography } from '@healthinal/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  getDocumentFile,
  useGetPersonDocumentsSuspense,
  type PersonDocumentDto,
} from '../../../../api/created/documents.ts';
import { DataTable, type DataTableColumn } from '../../../../components/table/DataTable.tsx';
import { formatDateWithGermanMonth } from '../../../../utils/date.ts';
import { getDocumentCategoryName } from '../../../../utils/document.ts';
import { base64ToBlob, downloadBlob, getMimeTypeFromFileExtension } from '../../../../utils/file.ts';

export const Route = createFileRoute('/masterdata/persons/$personId/document')({
  component: PersonDocumentRoute,
  staticData: { title: 'Dokumente' },
});

function DocumentRowActions({
  document,
  isDownloading,
  onDownload,
}: {
  document: PersonDocumentDto;
  isDownloading: boolean;
  onDownload: (document: PersonDocumentDto) => void;
}) {
  return (
    <Stack direction="row" gap={0.5} onClick={(e) => e.stopPropagation()}>
      <Tooltip title="Herunterladen">
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          loading={isDownloading}
          onClick={() => onDownload(document)}>
          <Icons.Download />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function PersonDocumentRoute() {
  const { personId } = Route.useParams();
  const { data: documents } = useGetPersonDocumentsSuspense(personId);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

  const handleDownload = async (document: PersonDocumentDto) => {
    setDownloadingFileId(document.fileId);
    try {
      const data = await getDocumentFile(document.fileId);
      const blob = base64ToBlob(data.file, getMimeTypeFromFileExtension(data.fileName ?? document.fileName));
      downloadBlob(blob, data.fileName ?? document.fileName);
    } catch {
      toast.error('Beim Herunterladen des Dokuments ist ein Fehler aufgetreten');
    } finally {
      setDownloadingFileId(null);
    }
  };

  const columns: DataTableColumn<PersonDocumentDto>[] = [
    {
      key: 'caseNumber',
      header: 'Fallnummer',
      width: 160,
      render: (document) => <Typography>{document.caseNumber ?? ''}</Typography>,
    },
    {
      key: 'category',
      header: 'Kategorie',
      width: 200,
      render: (document) => <Typography>{getDocumentCategoryName(document.category)}</Typography>,
    },
    {
      key: 'fileName',
      header: 'Dateiname',
      render: (document) => <Typography>{document.fileName}</Typography>,
    },
    {
      key: 'lastUpdated',
      header: 'Zuletzt aktualisiert',
      width: 180,
      render: (document) => <Typography>{formatDateWithGermanMonth(document.lastUpdated)}</Typography>,
    },
    {
      key: 'actions',
      header: 'Aktion',
      width: 80,
      disableTooltip: true,
      render: (document) => (
        <DocumentRowActions
          document={document}
          isDownloading={downloadingFileId === document.fileId}
          onDownload={handleDownload}
        />
      ),
    },
  ];

  return (
    <Stack flex={1} gap={2}>
      <Typography level="h3">Dokumente</Typography>
      <DataTable
        columns={columns}
        rows={documents}
        getRowKey={(document) => document.fileId}
        emptyText="Keine Dokumente vorhanden."
      />
    </Stack>
  );
}
